import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL =
    "https://customer-assets-jai6qajn.emergentagent.net/job_pdf-guide-dev/artifacts/7s3f15d0_HDB%20LOGO.webp";

export default function Login() {
    const nav = useNavigate();
    const loc = useLocation();
    const { user, refresh } = useAuth();
    const [mode, setMode] = useState("google"); // google | password
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user) nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
    }, [user, nav, loc.state]);

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const pwLogin = async () => {
        setBusy(true);
        try {
            await api.post("/auth/password-login", { email, password });
            await refresh();
            toast.success("Signed in");
            nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
        } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); }
        setBusy(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
            <div className="w-full max-w-md" data-testid="login-page">
                <div className="flex flex-col items-center mb-6">
                    <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
                        <img src={LOGO_URL} alt="HDB" className="h-16" />
                    </div>
                    <h1 className="hdb-heading font-black text-slate-900 text-2xl">HDB Billin App</h1>
                    <p className="text-xs text-slate-500 mt-1">Har Din Bachat · Invoicing & GST</p>
                </div>

                <div className="hdb-card p-6">
                    <div className="flex gap-2 mb-6">
                        <button onClick={() => setMode("google")}
                                className={`flex-1 py-2 rounded-md text-xs font-semibold ${mode === "google" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
                                data-testid="tab-google">
                            Google sign-in
                        </button>
                        <button onClick={() => setMode("password")}
                                className={`flex-1 py-2 rounded-md text-xs font-semibold ${mode === "password" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
                                data-testid="tab-password">
                            Username / Password
                        </button>
                    </div>

                    {mode === "google" ? (
                        <div>
                            <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                                Sign in with your Google account. Your role will be auto-assigned by the admin.
                            </p>
                            <button onClick={googleLogin} className="hdb-btn hdb-btn-primary w-full justify-center"
                                    data-testid="google-signin-btn">
                                <LogIn size={14} /> Continue with Google
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className="hdb-label">Email or Username</label>
                                <input className="hdb-input" type="text" value={email}
                                       onChange={(e) => setEmail(e.target.value)}
                                       placeholder="admin@company.com  or  rakesh"
                                       autoComplete="username" data-testid="login-email" />
                            </div>
                            <div>
                                <label className="hdb-label">Password</label>
                                <input className="hdb-input" type="password" value={password}
                                       onChange={(e) => setPassword(e.target.value)}
                                       autoComplete="current-password" data-testid="login-password"
                                       onKeyDown={(e) => e.key === "Enter" && pwLogin()} />
                            </div>
                            <button onClick={pwLogin} disabled={busy}
                                    className="hdb-btn hdb-btn-primary w-full justify-center"
                                    data-testid="login-submit">
                                <ShieldCheck size={14} /> {busy ? "Signing in…" : "Sign in"}
                            </button>
                            <div className="text-[11px] text-slate-500 text-center">
                                Ask your admin to create your login. Admins can set username &amp; password from Users &amp; Roles.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
